import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchTickets } from '@/lib/admin/tickets';
import type { TicketStatus, TicketPriority, TicketCategory } from '@/types/admin';
import { cn } from '@/lib/cn';

const STATUS_STYLES: Record<TicketStatus, string> = {
  OPEN: 'bg-sky-100 text-sky-700',
  PENDING: 'bg-amber-100 text-amber-700',
  IN_PROGRESS: 'bg-violet-100 text-violet-700',
  RESOLVED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-100 text-gray-700',
};
const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  NORMAL: 'bg-sky-100 text-sky-700',
  HIGH: 'bg-amber-100 text-amber-700',
  URGENT: 'bg-rose-100 text-rose-700',
};

export default function TicketsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get('status') ?? '') as TicketStatus | '';
  const priority = (searchParams.get('priority') ?? '') as TicketPriority | '';
  const category = (searchParams.get('category') ?? '') as TicketCategory | '';
  const page = Number(searchParams.get('page')) || 1;
  const perPage = 25;

  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const { data } = useQuery({
    queryKey: ['admin', 'tickets', { status, priority, category, q: debounced, page }],
    queryFn: () =>
      fetchTickets({
        status: status || undefined,
        priority: priority || undefined,
        category: category || undefined,
        q: debounced || undefined,
        page,
        perPage,
      }),
    placeholderData: keepPreviousData,
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.tickets')}</h1>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder={t('common.search')}
            className="min-w-[200px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setParam('q', e.target.value);
            }}
          />
          <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={status} onChange={(e) => setParam('status', e.target.value)}>
            <option value="">{t('admin.filters.allStatuses')}</option>
            {(['OPEN', 'PENDING', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as TicketStatus[]).map((s) => (
              <option key={s} value={s}>{t(`support.status.${s}`)}</option>
            ))}
          </select>
          <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={priority} onChange={(e) => setParam('priority', e.target.value)}>
            <option value="">{t('admin.filters.allPriorities')}</option>
            {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as TicketPriority[]).map((p) => (
              <option key={p} value={p}>{t(`support.priority.${p}`)}</option>
            ))}
          </select>
          <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={category} onChange={(e) => setParam('category', e.target.value)}>
            <option value="">{t('admin.filters.allCategories')}</option>
            {(['BILLING', 'BUG', 'FEATURE_REQUEST', 'QUESTION', 'OTHER'] as TicketCategory[]).map((c) => (
              <option key={c} value={c}>{t(`support.category.${c}`)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2">{t('admin.columns.subject')}</th>
              <th className="px-4 py-2">{t('admin.columns.user')}</th>
              <th className="px-4 py-2">{t('admin.columns.status')}</th>
              <th className="px-4 py-2">{t('admin.columns.priority')}</th>
              <th className="px-4 py-2">{t('admin.columns.category')}</th>
              <th className="px-4 py-2">{t('admin.columns.updated')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.noResults')}</td></tr>
            )}
            {items.map((t2) => (
              <tr key={t2.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/tickets/${t2.id}`)}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{t2.subject}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{t2.user?.email ?? '—'}</td>
                <td className="px-4 py-3"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_STYLES[t2.status])}>{t(`support.status.${t2.status}`)}</span></td>
                <td className="px-4 py-3"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', PRIORITY_STYLES[t2.priority])}>{t(`support.priority.${t2.priority}`)}</span></td>
                <td className="px-4 py-3 text-xs uppercase text-gray-600">{t2.category}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(t2.updatedAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
          <span>{t('common.showing')} {items.length} {t('common.of')} {total}</span>
          <div className="flex items-center gap-2">
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>‹</button>
            <span>{page} / {totalPages}</span>
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page >= totalPages} onClick={() => setParam('page', String(page + 1))}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
